import { useState } from "preact/hooks"
import { api_fetch_throws } from "@/api/client.tsx"
import { UserState } from "@/auth/user.tsx"
import { formatDistributor, toISODateString, useAsyncEffect } from "@/utils"

/** A `deduction.source` and the display name the server renders for it. */
export interface CustomerRef {
  name: string
  value: string
}

/**
 * The org's customers, named the way the server names them (`db::format_distributor`) — the same
 * mapping behind the customer filter and the dashboard export.
 *
 * Prefer this over `formatDistributor` anywhere a raw `deduction.source` is shown. That helper
 * only capital-cases a value it does not recognise, so it disagrees with the server on 19 of the
 * 53 sources ("Agne" for "Associated Grocers of New England", "Hyvee" for "Hy-Vee"). The fetch is
 * cached by `api_fetch_throws`, so components sharing this hook share one request.
 */
export function useCustomerRefs(): { refs: CustomerRef[]; label_for: (source: string) => string } {
  const [refs, setRefs] = useState<CustomerRef[]>([])
  const org_id = UserState.use(u => u?.org_id ?? null)

  useAsyncEffect(async () => {
    setRefs(await fetch_customer_refs())
  }, [org_id])

  const labels = customer_label_map(refs)
  return { refs, label_for: (source: string) => customer_label(source, labels) }
}

/** The page's date window, for listing only the customers with rows in it. */
export interface CustomerWindow {
  start_date: Date | null | undefined
  end_date: Date | null | undefined
  date_kind: string
}

/**
 * The one place that knows where customer display names come from. With a window, only the
 * customers with rows in it — including each name inside Other (`other:<name>`, the server's
 * `CustomerKey`); without one, every customer the org has had.
 */
export async function fetch_customer_refs(window?: CustomerWindow): Promise<CustomerRef[]> {
  const params: Record<string, string> = {}
  if (window?.start_date) params.start_date = toISODateString(window.start_date)
  if (window?.end_date) params.end_date = toISODateString(window.end_date)
  if (window) params.date_kind = window.date_kind
  return await api_fetch_throws<CustomerRef[]>("/deduction/distributors", { use_cache: true, params })
}

/**
 * The `deduction.source` a customer filter value names. An Other name (`other:Rock Island`) is a
 * row of source `other`; use this wherever a value is compared to a row's `source` on the client.
 */
export function customer_source(value: string): string {
  return value.startsWith("other:") ? "other" : value
}

/** The options to show: the window's, plus the current selection when the window has none of it. */
export function customer_choices_with_selection(choices: CustomerRef[], selected: string | null, all_time: CustomerRef[]): CustomerRef[] {
  if (!selected || choices.some(choice => choice.value === selected)) return choices
  const known = all_time.find(ref => ref.value === selected)
  return [...choices, known ?? { value: selected, name: selected.startsWith("other:") ? selected.slice(6) : formatDistributor(selected) }]
}

export function customer_label_map(refs: CustomerRef[]): Record<string, string> {
  return Object.fromEntries(refs.map(ref => [ref.value, ref.name]))
}

/**
 * The display name for a `deduction.source`. Falls back to local formatting for a source the
 * org's list does not carry, and while that list is still loading.
 */
export function customer_label(source: string, labels: Record<string, string>): string {
  return labels[source] ?? formatDistributor(source)
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const refs: CustomerRef[] = [
    { value: "kehe", name: "KeHE" },
    { value: "agne", name: "Associated Grocers of New England" },
    { value: "hyvee", name: "Hy-Vee" },
  ]

  describe("customer_source", () => {
    it("reads an Other name as source other", () => {
      expect(customer_source("other:Rock Island")).toBe("other")
      expect(customer_source("kehe")).toBe("kehe")
    })
  })

  describe("customer_choices_with_selection", () => {
    it("keeps a selection the window has no rows for", () => {
      const window_refs: CustomerRef[] = [{ value: "kehe", name: "KeHE" }]
      const all_time: CustomerRef[] = [...window_refs, { value: "other:Ocado", name: "Ocado" }]
      expect(customer_choices_with_selection(window_refs, "other:Ocado", all_time).map(r => r.name)).toEqual(["KeHE", "Ocado"])
      expect(customer_choices_with_selection(window_refs, "kehe", all_time)).toEqual(window_refs)
      expect(customer_choices_with_selection(window_refs, null, all_time)).toEqual(window_refs)
    })
  })

  describe("customer_label", () => {
    it("uses the server's name over local formatting", () => {
      const labels = customer_label_map(refs)
      // formatDistributor only capital-cases these two, which is how the chart legend and the
      // export came to disagree.
      expect(customer_label("agne", labels)).toBe("Associated Grocers of New England")
      expect(customer_label("hyvee", labels)).toBe("Hy-Vee")
      expect(customer_label("kehe", labels)).toBe("KeHE")
    })

    it("falls back to local formatting before the list loads", () => {
      expect(customer_label("kehe", {})).toBe("KeHE")
      expect(customer_label("some new source", {})).toBe("Some New Source")
    })

    it("falls back for a source the org's list does not carry", () => {
      expect(customer_label("unfi", customer_label_map(refs))).toBe("UNFI")
    })
  })
}
